import dayjs from 'dayjs'
import * as yup from 'yup'

import { AttendingPhysician, Gender, GetMyInfosQuery } from '@/core/graphql/types'

export type SettingsFormData = yup.InferType<typeof mainInfo> & {
  gender: Gender
}

export const mainInfo = yup.object({
  name: yup.string().required(),
  surname: yup.string().required(),
  secu: yup
    .string()
    .matches(/^\d+$/)
    .max(15)
    .min(15)
    .nullable()
    .transform((value) => value || undefined),
  gender: yup.string().oneOf(Object.values(Gender), 'Invalid gender').required(),
  phone: yup.string().optional(),
  email: yup.string().email().required(),
  city: yup.string().optional(),
  street: yup.string().optional(),
  cp: yup.string().optional(),
  birthday: yup.date().required(),
  country: yup.string().optional(),
  establishment: yup.string().optional(),
})

export const treatingDoctor = yup.object({
  name: yup.string().required(),
  surname: yup.string().required(),
  phone: yup.string().optional(),
  email: yup.string().email().required(),
  gender: yup.string().oneOf(['M', 'F', 'Autre'], 'Invalid gender').required(),
})

export const contactInfo = yup.object({
  name: yup.string().required(),
  surname: yup.string().required(),
  phone: yup.string().optional(),
  email: yup.string().email().required(),
  gender: yup.string().oneOf(Object.values(Gender), 'Invalid gender').required(),
})

export const insuranceInfo = yup.object({
  insured: yup.string().optional(),
  insuredRelationship: yup.string().optional(),
  insuranceProvider: yup.string().optional(),
  primaryInsurance: yup.string().optional(),
  secondaryInsurance: yup.string().optional(),
  primaryPolicy: yup.string().optional(),
  secondaryPolicy: yup.string().optional(),
  primaryPharmacy: yup.string().optional(),
})

export type treatingDoctorFormData = yup.InferType<typeof treatingDoctor>
export type contactInfoFormData = yup.InferType<typeof contactInfo>
export type insuranceInfoFormData = yup.InferType<typeof insuranceInfo>

const getOrDefault = (value: string | undefined | null, defaultValue: string = '') => value || defaultValue

export function getPatientDefaultValues(patient: GetMyInfosQuery['myInfos'] | undefined | null) {
  return {
    name: getOrDefault(patient?.userInfo.name),
    surname: getOrDefault(patient?.userInfo.surname),
    gender: getOrDefault(patient?.userInfo.gender) as Gender,
    secu: getOrDefault(patient?.secu),
    birthday: patient?.userInfo.birthday ? dayjs(patient.userInfo.birthday).toDate() : undefined,
    email: getOrDefault(patient?.email),
    phone: getOrDefault(patient?.contactInfo?.phone),
    city: getOrDefault(patient?.contactInfo?.city),
    street: getOrDefault(patient?.contactInfo?.street),
    cp: getOrDefault(patient?.contactInfo?.cp?.toString()),
    country: getOrDefault(patient?.contactInfo?.country),
    establishment: getOrDefault(patient?.establishment?._id),
  }
}

export const getAttendingPhysicianDefaultValues = (doctor: AttendingPhysician | undefined | null) => ({
  name: getOrDefault(doctor?.firstname),
  surname: getOrDefault(doctor?.lastname),
  gender: getOrDefault(doctor?.gender) as treatingDoctorFormData['gender'],
  email: getOrDefault(doctor?.email),
  phone: getOrDefault(doctor?.phone),
})

export function getParentDefaultValues(parents: GetMyInfosQuery['myInfos']['parents'] | undefined | null): Partial<contactInfoFormData>[] {
  if (!parents || parents.length === 0) {
    return [
      {
        name: '',
        surname: '',
        email: '',
        phone: '',
      },
    ]
  }

  return parents.map((parent) => ({
    firstname: parent?.firstname || '',
    lastname: parent?.lastname || '',
    email: parent?.email || '',
    phone: parent?.phone || '',
  }))
}

export const getInsuranceDefaultValues = (patient: GetMyInfosQuery['myInfos'] | undefined | null) => ({
  insured: getOrDefault(patient?.insured),
  insuredRelationship: getOrDefault(patient?.insuredRelationship),
  insuranceProvider: getOrDefault(patient?.insuranceProvider),
  primaryInsurance: getOrDefault(patient?.primaryInsurance),
  secondaryInsurance: getOrDefault(patient?.secondaryInsurance),
  primaryPolicy: getOrDefault(patient?.primaryPolicy),
  secondaryPolicy: getOrDefault(patient?.secondaryPolicy),
  primaryPharmacy: getOrDefault(patient?.primaryPharmacy),
})
